import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Phone, Mail, MessageCircle } from "lucide-react";
import { SERVICES, BRAND, PAGE_CONTENT } from "@/lib/constants";
import MagneticButton from "@/components/MagneticButton";

const EVENT_TYPES = ["Wedding", "Reception", "Corporate", "Birthday", "Anniversary", "Brand Activation", "Other"];

export default function Contact() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    event_type: "",
    event_date: "",
    services: [],
    guest_count: 150,
    name: "",
    phone: "",
    email: "",
    city: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (title) =>
    setForm((f) => ({ ...f, services: f.services.includes(title) ? f.services.filter((s) => s !== title) : [...f.services, title] }));

  const canNext = () => {
    if (step === 1) return !!form.event_type && !!form.event_date;
    if (step === 2) return form.services.length > 0;
    if (step === 3) return !!form.name.trim() && /^\+?[0-9\s-]{7,15}$/.test(form.phone.trim());
    return true;
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "32e90e5a-9e23-4e9b-9179-4248b146fdf2",
          subject: `KV Eventech Lead: ${form.event_type} - ${form.name}`,
          ...form,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        // Auto-build WhatsApp message & open it
        const waMsg = encodeURIComponent(
          `Hi KV Eventech 👋\n\nI'd like a quote.\n\nName: ${form.name}\nPhone: ${form.phone}\nCity: ${form.city}\nEvent: ${form.event_type} on ${form.event_date}\nGuests: ${form.guest_count}\nServices: ${form.services.join(", ")}\n\n${form.message}`
        );
        setTimeout(() => {
          window.open(`https://wa.me/${BRAND.whatsapp}?text=${waMsg}`, "_blank");
        }, 600);
      } else {
        setError(result.message || "Something went wrong — please call us directly.");
      }
    } catch (e) {
      setError("Network error — please call or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-5">{PAGE_CONTENT.contact.hero.kicker}</div>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter">
            {PAGE_CONTENT.contact.hero.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.contact.hero.title2}</em> {PAGE_CONTENT.contact.hero.title3}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-parchment/70 leading-relaxed">
            {PAGE_CONTENT.contact.hero.desc}
          </p>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-20 grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-8 bg-surface border border-line p-8 md:p-12">
          {/* Stepper */}
          <div className="flex items-center gap-3 mb-12">
            {[1, 2, 3, 4].map((n, i) => (
              <div key={n} className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 flex items-center justify-center font-display text-xs border ${
                    step >= n ? "bg-copper text-ink border-copper" : "border-line text-parchment/50"
                  }`}
                  data-testid={`step-indicator-${n}`}
                >
                  {step > n ? <Check size={14} /> : n}
                </div>
                {i < 3 && <div className={`h-px w-10 ${step > n ? "bg-copper" : "bg-line"}`} />}
              </div>
            ))}
            <div className="ml-auto font-display text-[10px] uppercase tracking-[0.3em] text-muted2">
              Step {step} / 4
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
              >
                {step === 1 && (
                  <div data-testid="booking-step-1">
                    <h2 className="font-serif text-3xl md:text-4xl mb-8">{PAGE_CONTENT.contact.steps.s1.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.contact.steps.s1.title2}</em></h2>
                    <label className="block font-display text-[10px] uppercase tracking-[0.3em] text-copper mb-3">Event Type</label>
                    <div className="flex flex-wrap gap-3 mb-8">
                      {EVENT_TYPES.map((t) => (
                        <button
                          key={t}
                          onClick={() => update("event_type", t)}
                          data-testid={`event-type-${t.toLowerCase()}`}
                          className={`px-4 py-2.5 font-display text-[11px] uppercase tracking-[0.25em] border transition-colors ${
                            form.event_type === t ? "border-copper bg-copper/10 text-copper" : "border-line text-parchment/80 hover:border-copper/60"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>

                    <label className="block font-display text-[10px] uppercase tracking-[0.3em] text-copper mb-3">Event Date</label>
                    <input
                      type="date"
                      value={form.event_date}
                      onChange={(e) => update("event_date", e.target.value)}
                      data-testid="booking-event-date"
                      className="w-full max-w-sm bg-ink border border-line p-4 text-parchment font-sans focus:border-copper"
                    />
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="booking-step-2">
                    <h2 className="font-serif text-3xl md:text-4xl mb-8">{PAGE_CONTENT.contact.steps.s2.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.contact.steps.s2.title2}</em></h2>
                    <div className="grid sm:grid-cols-2 gap-3 mb-8">
                      {SERVICES.map((s) => {
                        const selected = form.services.includes(s.title);
                        return (
                          <button
                            key={s.slug}
                            onClick={() => toggleService(s.title)}
                            data-testid={`service-select-${s.slug}`}
                            className={`text-left p-4 border transition-colors ${
                              selected ? "border-copper bg-copper/10" : "border-line hover:border-copper/60"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-display text-[10px] uppercase tracking-[0.25em] text-copper mb-1">{s.num}</div>
                                <div className="font-serif text-xl">{s.title}</div>
                              </div>
                              <div className={`w-6 h-6 border flex items-center justify-center ${selected ? "bg-copper border-copper" : "border-line"}`}>
                                {selected && <Check size={14} className="text-ink" />}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    <label className="block font-display text-[10px] uppercase tracking-[0.3em] text-copper mb-3">
                      Guest Count: <span className="text-parchment ml-2">{form.guest_count}</span>
                    </label>
                    <input
                      type="range"
                      min={20}
                      max={1000}
                      step={10}
                      value={form.guest_count}
                      onChange={(e) => update("guest_count", Number(e.target.value))}
                      data-testid="booking-guest-count"
                      className="w-full accent-copper"
                    />
                    <div className="flex justify-between font-display text-[10px] uppercase tracking-[0.25em] text-muted2 mt-2">
                      <span>20</span><span>500</span><span>1000+</span>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="booking-step-3">
                    <h2 className="font-serif text-3xl md:text-4xl mb-8">{PAGE_CONTENT.contact.steps.s3.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.contact.steps.s3.title2}</em></h2>
                    <div className="grid md:grid-cols-2 gap-5">
                      <FormField label="Full Name" value={form.name} onChange={(v) => update("name", v)} testId="field-name" required />
                      <FormField label="Phone (WhatsApp preferred)" value={form.phone} onChange={(v) => update("phone", v)} testId="field-phone" required type="tel" />
                      <FormField label="Email (optional)" value={form.email} onChange={(v) => update("email", v)} testId="field-email" type="email" />
                      <FormField label="City" value={form.city} onChange={(v) => update("city", v)} testId="field-city" />
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div data-testid="booking-step-4">
                    <h2 className="font-serif text-3xl md:text-4xl mb-8">{PAGE_CONTENT.contact.steps.s4.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.contact.steps.s4.title2}</em></h2>
                    <textarea
                      rows={6}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Theme, colour palette, venue, must-haves…"
                      data-testid="field-message"
                      className="w-full bg-ink border border-line p-4 text-parchment font-sans focus:border-copper"
                    />

                    <div className="mt-8 p-6 bg-ink border border-line">
                      <div className="font-display text-[10px] uppercase tracking-[0.3em] text-copper mb-4">Review</div>
                      <div className="grid md:grid-cols-2 gap-3 text-sm font-sans text-parchment/80">
                        <div><b className="text-parchment">Event:</b> {form.event_type} · {form.event_date}</div>
                        <div><b className="text-parchment">Guests:</b> {form.guest_count}</div>
                        <div><b className="text-parchment">Name:</b> {form.name}</div>
                        <div><b className="text-parchment">Phone:</b> {form.phone}</div>
                        {form.email && <div><b className="text-parchment">Email:</b> {form.email}</div>}
                        {form.city && <div><b className="text-parchment">City:</b> {form.city}</div>}
                        <div className="md:col-span-2"><b className="text-parchment">Services:</b> {form.services.join(", ")}</div>
                      </div>
                    </div>
                    {error && <div className="mt-4 text-red-400 text-sm" data-testid="form-error">{error}</div>}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-center py-16"
                data-testid="booking-success"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-copper text-ink rounded-full mb-8">
                  <Check size={30} />
                </div>
                <h2 className="font-serif text-4xl md:text-5xl mb-6">{PAGE_CONTENT.contact.success.title}</h2>
                <p className="text-parchment/75 max-w-lg mx-auto mb-10 leading-relaxed">
                  {PAGE_CONTENT.contact.success.desc}
                </p>
                <MagneticButton
                  as="a"
                  href={`https://wa.me/${BRAND.whatsapp}`}
                  testId="success-whatsapp"
                >
                  <MessageCircle size={14} /> Chat on WhatsApp
                </MagneticButton>
              </motion.div>
            )}
          </AnimatePresence>

          {!submitted && (
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-line">
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                data-testid="booking-prev"
                className="font-display text-[11px] uppercase tracking-[0.3em] text-parchment/70 hover:text-copper disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ← Back
              </button>
              {step < 4 ? (
                <MagneticButton
                  onClick={() => canNext() && setStep((s) => s + 1)}
                  testId="booking-next"
                  className={!canNext() ? "opacity-50" : ""}
                >
                  Continue <ArrowRight size={14} />
                </MagneticButton>
              ) : (
                <MagneticButton
                  onClick={submit}
                  testId="booking-submit"
                  className={loading ? "opacity-60" : ""}
                >
                  {loading ? "Sending…" : "Submit Enquiry"} <ArrowRight size={14} />
                </MagneticButton>
              )}
            </div>
          )}
        </div>

        {/* Aside */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-surface border border-line p-8">
            <div className="font-display text-[11px] uppercase tracking-[0.3em] text-copper mb-5">{PAGE_CONTENT.contact.aside.preferDirect}</div>
            <div className="space-y-5 font-sans">
              <a href={`tel:${BRAND.phone}`} data-testid="aside-phone" className="flex items-center gap-3 group">
                <Phone size={18} className="text-copper" />
                <span className="font-serif text-2xl group-hover:text-copper transition-colors">{BRAND.phoneDisplay}</span>
              </a>
              <a href={`https://wa.me/${BRAND.whatsapp}`} data-testid="aside-whatsapp" target="_blank" rel="noreferrer" className="flex items-center gap-3 group">
                <MessageCircle size={18} className="text-copper" />
                <span className="font-serif text-xl group-hover:text-copper transition-colors">WhatsApp us</span>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="aside-email" className="flex items-center gap-3 group">
                <Mail size={18} className="text-copper" />
                <span className="font-serif text-lg group-hover:text-copper transition-colors">{BRAND.email}</span>
              </a>
            </div>
          </div>

          <div className="bg-surface border border-line p-8">
            <div className="font-display text-[11px] uppercase tracking-[0.3em] text-copper mb-4">{PAGE_CONTENT.contact.aside.leadTime.kicker}</div>
            <p className="font-serif text-xl leading-snug text-parchment/80">
              {PAGE_CONTENT.contact.aside.leadTime.desc}
            </p>
          </div>

          <div className="bg-surface border border-line p-8">
            <div className="font-display text-[11px] uppercase tracking-[0.3em] text-copper mb-4">{PAGE_CONTENT.contact.aside.coverage.kicker}</div>
            <p className="font-serif text-xl leading-snug text-parchment/80">
              {PAGE_CONTENT.contact.aside.coverage.desc}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function FormField({ label, value, onChange, testId, type = "text", required }) {
  return (
    <div>
      <label className="block font-display text-[10px] uppercase tracking-[0.3em] text-copper mb-3">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-ink border border-line p-4 text-parchment font-sans focus:border-copper"
      />
    </div>
  );
}
